import React, { useCallback, useEffect, useState } from "react";
import "./payment.css";
import { HiOutlineCurrencyDollar } from "react-icons/hi2";
import { FaRegCreditCard, FaRegSmile } from "react-icons/fa";
import { FiShield, FiTruck, FiInfo } from "react-icons/fi";
import CheckoutNavbar from "../CheckoutNavbar/CheckoutNavbar";
import StripePaymentSection from "./StripePaymentSection";
import axiosInstance from "../../utils/axiosInstance";
import { clearPaymentIntentIdempotencyKey } from "../../utils/paymentApi";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearBuyCart, clearRentCart } from "../../redux/cartSlice";
import Breadcrumbs from "../Breadcrumbs";

// ─── Price helpers ────────────────────────────────────────────────────────────
const getLinePrice = (item) => {
  if (item.offerPrice != null) return Number(item.offerPrice);
  if (item.selectedPackage?.offer_price) return Number(item.selectedPackage.offer_price);
  return Number(item.sale_offer_price ?? item.sale_price ?? item.oldPrice ?? item.rental_price ?? 0);
};

function Payment() {
  const [active, setActive] = useState("cash");
  const [processing, setProcessing] = useState(false);
  const [completingRedirect, setCompletingRedirect] = useState(false);
  // orderId is set from localStorage or AFTER the order is created on Place Order click
  const [orderId, setOrderId] = useState(() => localStorage.getItem("orderId") || null);
  const [orderInfo, setOrderInfo] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const existingOrderId = localStorage.getItem("orderId");
    if (existingOrderId && existingOrderId !== "null" && existingOrderId !== "undefined") {
      setOrderId(existingOrderId);
      axiosInstance.get(`/accounts/orders/${existingOrderId}/`)
        .then((res) => {
          const data = Array.isArray(res?.data) ? res.data[0] : (res?.data?.order || res?.data);
          setOrderInfo(data);
        })
        .catch((err) => console.error("Error fetching order on payment page:", err));
    }
  }, []);

  // ── Read cart from Redux ──────────────────────────────────────────────────
  const { buyCart = [], rentCart = [] } = useSelector((store) => store.cart || {});

  const saleAddressRaw = localStorage.getItem("saleAddress");
  const rentalAddressRaw = localStorage.getItem("rentalAddress");
  const defaultAddressId = localStorage.getItem("defaultAddressId");
  const saleAddressId = saleAddressRaw
    ? JSON.parse(saleAddressRaw)?.id
    : (defaultAddressId ? Number(defaultAddressId) : null);
  const rentalAddressId = rentalAddressRaw ? JSON.parse(rentalAddressRaw)?.id : "";

  const allItems = [
    ...buyCart.map((item) => ({ ...item, cartType: "Sale" })),
    ...rentCart.map((item) => ({ ...item, cartType: "Rental" })),
  ];

  // ── Compute totals from cart / fetched order ─────────────────────────────
  const orderData = Array.isArray(orderInfo) ? orderInfo[0] : (orderInfo?.order || orderInfo);
  const currentDeliveryMode = localStorage.getItem("deliveryMode") || orderData?.delivery_mode || "company-transport";
  // Ignore the flawed API delivery charges from orderData, rely purely on the delivery API calculation (localStorage)
  const deliveryCharge = currentDeliveryMode === "self-transport" ? 0 : Number(localStorage.getItem("deliveryCharge") || 0);

  const cartSubtotal = orderData?.total_amount != null
    ? Number(orderData.total_amount)
    : allItems.reduce((sum, item) => sum + getLinePrice(item) * (item.qty || 1), 0);

  const totalDiscount = Number(orderData?.sale_discount_amount || 0) + Number(orderData?.rental_discount_amount || 0);

  // Guarantee the math matches exactly
  const payableAmount = Math.max(0, cartSubtotal + deliveryCharge - totalDiscount);

  // ── Build order payload ──────────────────────────────────────────────────
  const buildOrderPayload = () => {
    const orderDetailsPayload = allItems.map((item) => {
      const variantId = item.rentalCharges?.variant || item.variantId || item.id;
      const isSale = item.cartType?.toLowerCase() === "sale";
      const basePayload = {
        variant: Number(variantId),
        item_type: isSale ? "sale" : "rental",
        quantity: item.qty || 1,
      };
      if (!isSale) {
        basePayload.rental_start_date =
          item.fromDate || item.rental_start_date || new Date().toISOString().split("T")[0];
        basePayload.rental_end_date = item.toDate || item.rentalCharges?.rental_end_date || undefined;
        basePayload.package_name = item.selectedPackage?.package_name || item.rentalPlan || "Daily";
        basePayload.duration_value = item.selectedPackage?.duration_value || item.duration || 1;
        basePayload.unique_id = item.cartItemId || item.unique_id || crypto.randomUUID();
      }
      return basePayload;
    });

    return {
      sale_addresses: buyCart.length > 0 ? (saleAddressId || "") : "",
      rental_addresses: rentCart.length > 0 ? (rentalAddressId || "") : "",
      delivery_mode: currentDeliveryMode,
      order_details: orderDetailsPayload,
    };
  };

  // ── Create or update order and return the orderId ────────────────────────
  const createOrder = async () => {
    const payload = buildOrderPayload();
    const existingOrderId = localStorage.getItem("orderId");
    if (existingOrderId && existingOrderId !== "null" && existingOrderId !== "undefined") {
      const res = await axiosInstance.patch(`/accounts/orders/${existingOrderId}/`, payload);
      const updatedOrderData = Array.isArray(res?.data) ? res.data[0] : (res?.data?.order || res?.data);
      const updatedOrderId = updatedOrderData?.id || res?.data?.id || res?.data?.order?.id || existingOrderId;
      setOrderId(updatedOrderId);
      if (updatedOrderData) setOrderInfo(updatedOrderData);
      return updatedOrderId;
    } else {
      const res = await axiosInstance.post("/accounts/orders/", payload);
      const newOrderData = Array.isArray(res?.data) ? res.data[0] : (res?.data?.order || res?.data);
      const newOrderId = newOrderData?.id || res?.data?.id || res?.data?.order?.id;
      if (!newOrderId) throw new Error("Order creation failed: no orderId returned.");
      setOrderId(newOrderId);
      localStorage.setItem("orderId", newOrderId);
      if (newOrderData) setOrderInfo(newOrderData);
      return newOrderId;
    }
  };

  // ── Cleanup after order is complete ──────────────────────────────────────
  const clearCheckoutSession = useCallback((completedOrderId) => {
    localStorage.removeItem("orderId");
    localStorage.removeItem("buyCart");
    localStorage.removeItem("rentCart");
    localStorage.removeItem("saleAddress");
    localStorage.removeItem("rentalAddress");
    localStorage.removeItem("deliveryCharge");
    dispatch(clearBuyCart());
    dispatch(clearRentCart());
    clearPaymentIntentIdempotencyKey(completedOrderId);
  }, [dispatch]);

  // ── Handle Stripe redirect return ────────────────────────────────────────
  // If Stripe redirects back with ?redirect_status=succeeded we need an orderId.
  // We store the orderId in sessionStorage just before Stripe redirect.
  const redirectOrderId = sessionStorage.getItem("pendingOrderId");

  const completeOnlineOrder = useCallback(async (resolvedOrderId) => {
    const payload = { status: "Placed", payment_mode: "online" };
    const res = await axiosInstance.post(
      `/accounts/orders/${resolvedOrderId}/order_status_update/`,
      payload
    );
    toast.success(res.data?.message || "Online payment successful! Order confirmed.");
    clearCheckoutSession(resolvedOrderId);
    sessionStorage.removeItem("pendingOrderId");
    navigate("/orders");
  }, [clearCheckoutSession, navigate]);

  useEffect(() => {
    const redirectStatus = searchParams.get("redirect_status");
    const paymentIntent = searchParams.get("payment_intent");
    if (redirectStatus !== "succeeded" || !paymentIntent || !redirectOrderId) return;

    let cancelled = false;
    async function finalizeRedirectPayment() {
      setCompletingRedirect(true);
      try {
        await completeOnlineOrder(redirectOrderId);
        if (!cancelled) setSearchParams({}, { replace: true });
      } catch (error) {
        console.error(error);
        toast.error(
          error.response?.data?.message ||
          "Payment succeeded but order confirmation failed. Contact support."
        );
      } finally {
        if (!cancelled) setCompletingRedirect(false);
      }
    }
    finalizeRedirectPayment();
    return () => { cancelled = true; };
  }, [completeOnlineOrder, redirectOrderId, searchParams, setSearchParams]);

  // ── COD: Create order THEN update status ─────────────────────────────────
  async function PlaceOrderByCOD() {
    if (allItems.length === 0) {
      toast.error("Your cart is empty.");
      navigate("/cart");
      return;
    }
    setProcessing(true);
    try {
      const newOrderId = await createOrder();
      const res = await axiosInstance.post(
        `/accounts/orders/${newOrderId}/order_status_update/`,
        { status: "Placed", payment_mode: "cash" }
      );
      toast.success(res.data?.message || "Order placed successfully via Cash on Delivery!");
      clearCheckoutSession(newOrderId);
      navigate("/orders");
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.details ||
        "Something went wrong placing the order.";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }

  // ── Online: Create order THEN hand off to Stripe ─────────────────────────
  const [stripeOrderId, setStripeOrderId] = useState(null);
  const [creatingOnlineOrder, setCreatingOnlineOrder] = useState(false);

  const handlePrepareOnlinePayment = async () => {
    if (allItems.length === 0) {
      toast.error("Your cart is empty.");
      navigate("/cart");
      return;
    }
    setCreatingOnlineOrder(true);
    try {
      const newOrderId = await createOrder();
      sessionStorage.setItem("pendingOrderId", String(newOrderId));
      setStripeOrderId(newOrderId);
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to initiate online payment. Please try again.";
      toast.error(msg);
    } finally {
      setCreatingOnlineOrder(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "$0.00";
    const formatted = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
    return `$${formatted}`;
  };

  return (
    <div className="payment-page-wrapper">

      {completingRedirect ? (
        <div className="payment-loading-container">
          <div className="payment-spinner"></div>
          <p>Confirming your payment…</p>
        </div>
      ) : (
        <div className="payment-layout-container">
          <Breadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Cart", path: "/cart" },
            { label: "Checkout", path: "/checkout" },
            { label: "Payment", path: "" }
          ]} />

          {/* Header Row */}
          <div className="payment-title-header">
            <h1>Secure Payment</h1>
            <p className="subtitle">Choose a payment method and finalize your transaction.</p>
          </div>

          <div className="payment-main-grid">

            {/* Left Side: Payment Options */}
            <div className="payment-methods-card">
              <div className="payment-methods-tabs">
                <button
                  type="button"
                  className={`tab-btn ${active === "cash" ? "active" : ""}`}
                  onClick={() => { setActive("cash"); setStripeOrderId(null); }}
                >
                  <span className="tab-icon">💸</span>
                  <div className="tab-label">
                    <strong>Cash on Delivery</strong>
                    <span>Pay at your convenience</span>
                  </div>
                </button>

                <button
                  type="button"
                  className={`tab-btn ${active === "online" ? "active" : ""}`}
                  onClick={() => setActive("online")}
                >
                  <span className="tab-icon"><FaRegCreditCard /></span>
                  <div className="tab-label">
                    <strong>Credit / Debit Card</strong>
                    <span>Instant checkout via safe gateway</span>
                  </div>
                </button>
              </div>

              <div className="payment-tab-content">
                {active === "cash" && (
                  <div className="cash-panel animate-fade-in">
                    <div className="info-alert">
                      <FiInfo className="alert-icon" />
                      <div className="alert-text">
                        <h5>Cash on Delivery (COD) Enabled</h5>
                        <p>You can pay via cash, card or digital UPI to the delivery executive when your shipment arrives at your designated address.</p>
                      </div>
                    </div>

                    <div className="cod-details-box">
                      <div className="cod-row">
                        <FiTruck className="cod-icon" />
                        <div>
                          <h6>Standard Home Delivery</h6>
                          <span>Ships immediately upon order confirmation.</span>
                        </div>
                      </div>
                      <div className="cod-row mt-3">
                        <FiShield className="cod-icon" />
                        <div>
                          <h6>Genuine Products Guaranteed</h6>
                          <span>Check products before making payment.</span>
                        </div>
                      </div>
                    </div>

                    <div className="payment-action-area">
                      <button
                        className="btn-pay-submit cod-btn"
                        onClick={PlaceOrderByCOD}
                        disabled={processing}
                      >
                        {processing ? (
                          <span className="btn-spinner"></span>
                        ) : (
                          <>
                            <span>PLACE ORDER (COD)</span>
                            <span className="price-badge">{formatPrice(payableAmount)}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {active === "online" && (
                  <div className="online-panel animate-fade-in">
                    <div className="info-alert stripe-info-alert">
                      <FiShield className="alert-icon" />
                      <div className="alert-text">
                        <h5>Secure card payment</h5>
                        <p>
                          Pay with credit or debit card via Stripe. Amount charged:{" "}
                          <strong>{formatPrice(payableAmount)}</strong>
                        </p>
                      </div>
                    </div>

                    {/* Step 1: Create order before showing Stripe form */}
                    {!stripeOrderId ? (
                      <div className="payment-action-area">
                        <button
                          className="btn-pay-submit cod-btn"
                          onClick={handlePrepareOnlinePayment}
                          disabled={creatingOnlineOrder}
                        >
                          {creatingOnlineOrder ? (
                            <span className="btn-spinner"></span>
                          ) : (
                            <>
                              <span>PROCEED TO CARD PAYMENT</span>
                              <span className="price-badge">{formatPrice(payableAmount)}</span>
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      /* Step 2: Show Stripe form with the created orderId */
                      <StripePaymentSection
                        orderId={stripeOrderId}
                        amountLabel={formatPrice(payableAmount)}
                        processing={processing}
                        setProcessing={setProcessing}
                        onPaymentSuccess={() => completeOnlineOrder(stripeOrderId)}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side: Order Summary from cart */}
            <div className="payment-summary-card">
              <div className="summary-title">
                <HiOutlineCurrencyDollar className="summary-dollar-icon" />
                <h3>Order Summary</h3>
              </div>

              <div className="order-summary-details">
                <div className="summary-info-row">
                  <span className="info-lbl">Payment Mode Status</span>
                  <span className="info-val badge-status">Awaiting Payment</span>
                </div>

                <hr className="divider" />

                <div className="summary-price-row">
                  <span>Cart Subtotal</span>
                  <strong>{formatPrice(cartSubtotal)}</strong>
                </div>

                <div className="summary-price-row">
                  <span>Transport</span>
                  <span className={deliveryCharge === 0 ? "free-shipping" : "info-val"}>
                    {deliveryCharge === 0 ? "Self Pickup" : formatPrice(deliveryCharge)}
                  </span>
                </div>

                {totalDiscount > 0 && (
                  <div className="summary-price-row discount-row" style={{ color: "#16a34a" }}>
                    <span>Coupon Discount</span>
                    <strong>-{formatPrice(totalDiscount)}</strong>
                  </div>
                )}

                <hr className="divider" />

                <div className="summary-price-row total">
                  <span>Net Amount Payable</span>
                  <span className="total-val">{formatPrice(payableAmount)}</span>
                </div>
              </div>

              <div className="security-guarantee-box">
                <FiShield className="guarantee-icon" />
                <div>
                  <h6>100% Encrypted Transactions</h6>
                  <p>All data is processed using industry-standard AES-256 secure network gateways.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default Payment;
